import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const API = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
API.interceptors.request.use((c) => { const t = localStorage.getItem("abs_token"); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const rp = (n) => (n == null ? "-" : `Rp ${Number(n).toLocaleString("id-ID")}`);
const badgeColor = (s) => ({
  NOT_STARTED: "bg-slate-500", IN_PROGRESS: "bg-blue-500", REVIEW: "bg-amber-500",
  COMPLETE: "bg-emerald-500", BLOCKED: "bg-red-600",
  TODO: "bg-slate-500", DONE: "bg-emerald-500", WAIVED: "bg-amber-500",
  INITIATED: "bg-slate-500", CLEARANCE: "bg-blue-500",
  FINAL_PAYROLL: "bg-indigo-500", ASSET_RETURN: "bg-teal-500",
  ACCESS_REVOCATION: "bg-purple-500", CANCELLED: "bg-red-500",
  PENDING: "bg-slate-500", APPROVED: "bg-emerald-500",
  OVERRIDDEN: "bg-orange-500",
  SESUAI: "bg-emerald-500", ERROR: "bg-red-600",
  ASSIGNED: "bg-blue-500", RETURNED: "bg-emerald-600",
  PASS: "bg-emerald-500", EXTEND: "bg-amber-500", FAIL: "bg-red-600",
  ACTIVE: "bg-blue-500", CLOSED: "bg-slate-500",
  COMPUTED: "bg-emerald-500", POSTED: "bg-emerald-700", DONE: "bg-emerald-500",
}[s] || "bg-slate-500");


// =========================================================
// ONBOARDING HUB
// =========================================================
export function OnboardingHub() {
  const [dash, setDash] = useState({});
  useEffect(() => { API.get("/onboarding-dashboard").then((r) => setDash(r.data)).catch(() => {}); }, []);
  return (
    <div className="p-6 space-y-4" data-testid="onboarding-hub">
      <h1 className="text-3xl font-bold">Onboarding — Tahap 8G</h1>
      <div className="grid md:grid-cols-4 gap-3">
        <Card><CardHeader><CardTitle className="text-sm">Onboarding Aktif</CardTitle></CardHeader><CardContent className="text-2xl font-bold" data-testid="stat-ob-active">{dash.onboarding_active ?? "-"}</CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Task Overdue</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-red-600" data-testid="stat-overdue">{dash.overdue_tasks ?? "-"}</CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Probation Aktif</CardTitle></CardHeader><CardContent className="text-2xl font-bold" data-testid="stat-prob">{dash.probation_active ?? "-"}</CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">New Joiner 30 Hari</CardTitle></CardHeader><CardContent className="text-2xl font-bold" data-testid="stat-new">{dash.new_joiners_30d ?? "-"}</CardContent></Card>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <a href="/onboarding/proses"><Card className="hover:shadow-md" data-testid="link-onb-list"><CardHeader><CardTitle>Proses Onboarding</CardTitle></CardHeader><CardContent>Mulai / kelola onboarding karyawan.</CardContent></Card></a>
        <a href="/onboarding/template"><Card className="hover:shadow-md" data-testid="link-onb-template"><CardHeader><CardTitle>Template Checklist</CardTitle></CardHeader><CardContent>Kelola template configurable per scope.</CardContent></Card></a>
        <a href="/onboarding/probation"><Card className="hover:shadow-md" data-testid="link-onb-prob"><CardHeader><CardTitle>Probation</CardTitle></CardHeader><CardContent>Kelola probation & result.</CardContent></Card></a>
        <a href="/onboarding/integritas"><Card className="hover:shadow-md" data-testid="link-onb-int"><CardHeader><CardTitle>Integritas Onboarding</CardTitle></CardHeader><CardContent>Cek konsistensi.</CardContent></Card></a>
      </div>
    </div>
  );
}


// =========================================================
// ONBOARDING LIST
// =========================================================
export function OnboardingListPage() {
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [form, setForm] = useState({ employee_id: "", start_date: "", probation_end_date: "", template_code: "" });
  const [templates, setTemplates] = useState([]);
  const reload = () => API.get("/onboarding").then((r) => setRows(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Gagal load"));
  useEffect(() => {
    reload();
    API.get("/hr/employees").then((r) => setEmps(r.data)).catch(() => {});
    API.get("/onboarding/templates").then((r) => setTemplates(r.data)).catch(() => {});
  }, []);
  const submit = async () => {
    try {
      await API.post("/onboarding/start", form);
      toast.success("Onboarding dimulai");
      reload();
      setForm({ employee_id: "", start_date: "", probation_end_date: "", template_code: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="onboarding-list-page">
      <h1 className="text-2xl font-bold">Proses Onboarding</h1>
      <Card>
        <CardHeader><CardTitle>Mulai Onboarding Baru</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-4 gap-3">
          <div><Label>Karyawan</Label>
            <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
              <SelectTrigger data-testid="ob-employee"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
              <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} — {e.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Tgl Mulai</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="ob-start" /></div>
          <div><Label>Probation Berakhir</Label><Input type="date" value={form.probation_end_date} onChange={(e) => setForm({ ...form, probation_end_date: e.target.value })} data-testid="ob-prob" /></div>
          <div><Label>Template (opsional)</Label>
            <Select value={form.template_code} onValueChange={(v) => setForm({ ...form, template_code: v })}>
              <SelectTrigger data-testid="ob-template"><SelectValue placeholder="Auto-resolve" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.code}>{t.code} — {t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="col-span-4"><Button onClick={submit} data-testid="ob-submit">Mulai Onboarding</Button></div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Daftar Onboarding</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>No</TableHead><TableHead>Karyawan</TableHead>
              <TableHead>Mulai</TableHead><TableHead>Progress</TableHead>
              <TableHead>Status</TableHead><TableHead>Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>{rows.map((r) => (
              <TableRow key={r.id} data-testid={`ob-row-${r.number}`}>
                <TableCell className="font-mono">{r.number}</TableCell>
                <TableCell>{r.employee_name}</TableCell>
                <TableCell>{r.start_date}</TableCell>
                <TableCell>{r.progress_pct ?? 0}%</TableCell>
                <TableCell><Badge className={badgeColor(r.status)}>{r.status}</Badge></TableCell>
                <TableCell><a className="text-blue-600 underline" href={`/onboarding/proses/${r.id}`}>Detail</a></TableCell>
              </TableRow>
            ))}</TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}


// =========================================================
// ONBOARDING DETAIL (tasks + workflow)
// =========================================================
export function OnboardingDetailPage() {
  const oid = window.location.pathname.split("/").pop();
  const [ob, setOb] = useState(null);
  const reload = () => API.get(`/onboarding/${oid}`).then((r) => setOb(r.data)).catch(() => {});
  useEffect(() => { reload(); }, [oid]);
  if (!ob) return <div className="p-6" data-testid="ob-detail-loading">Memuat…</div>;
  const transition = async (ep, body = {}) => {
    try { await API.post(`/onboarding/${oid}/${ep}`, body); toast.success("OK"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const setTaskStatus = async (code, status) => {
    try { await API.put(`/onboarding/${oid}/tasks/${code}`, { status }); toast.success("Task OK"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const waive = async (code) => {
    const reason = window.prompt("Alasan waiver (wajib, min 3 karakter):");
    if (!reason || reason.length < 3) return;
    try { await API.post(`/onboarding/${oid}/tasks/${code}/waive`, { reason }); toast.success("Waived"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="ob-detail-page">
      <h1 className="text-2xl font-bold">Onboarding {ob.number}</h1>
      <Card><CardContent className="grid grid-cols-4 gap-3 pt-4">
        <div><Label className="text-xs">Karyawan</Label><div>{ob.employee_name}</div></div>
        <div><Label className="text-xs">Status</Label><div><Badge className={badgeColor(ob.status)}>{ob.status}</Badge></div></div>
        <div><Label className="text-xs">Progress</Label><div>{ob.progress_pct}%</div></div>
        <div className="space-x-2">
          {ob.status === "NOT_STARTED" && <Button size="sm" onClick={() => transition("start-progress")} data-testid="ob-start-progress">Mulai</Button>}
          {ob.status === "IN_PROGRESS" && <Button size="sm" onClick={() => transition("submit-review")} data-testid="ob-submit-review">Submit Review</Button>}
          {ob.status === "REVIEW" && <>
            <Button size="sm" onClick={() => transition("complete")} data-testid="ob-complete">Complete</Button>
            <Button size="sm" variant="outline" onClick={() => {
              const reason = window.prompt("Alasan override:");
              if (reason) transition("complete", { override: true, override_reason: reason });
            }} data-testid="ob-complete-override">Complete (Override)</Button>
          </>}
        </div>
      </CardContent></Card>
      <Card><CardHeader><CardTitle>Checklist Task</CardTitle></CardHeader><CardContent>
        <Table><TableHeader><TableRow>
          <TableHead>Task</TableHead><TableHead>Kategori</TableHead><TableHead>Kritikal</TableHead>
          <TableHead>Due</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead>
        </TableRow></TableHeader>
        <TableBody>{(ob.tasks || []).map((t) => (
          <TableRow key={t.code} data-testid={`ob-task-${t.code}`}>
            <TableCell><b>{t.code}</b><br/><span className="text-xs text-slate-500">{t.name}</span></TableCell>
            <TableCell>{t.category || "-"}</TableCell>
            <TableCell>{t.critical ? "Ya" : "-"}</TableCell>
            <TableCell>{t.due_date || "-"}</TableCell>
            <TableCell><Badge className={badgeColor(t.status)}>{t.status}</Badge>{t.waived_reason && <div className="text-xs italic mt-1">"{t.waived_reason}"</div>}</TableCell>
            <TableCell className="space-x-1">
              {t.status === "TODO" && <Button size="sm" onClick={() => setTaskStatus(t.code, "IN_PROGRESS")}>Start</Button>}
              {t.status === "IN_PROGRESS" && <Button size="sm" onClick={() => setTaskStatus(t.code, "DONE")}>Done</Button>}
              {(t.status === "TODO" || t.status === "IN_PROGRESS" || t.status === "BLOCKED") && <Button size="sm" variant="outline" onClick={() => waive(t.code)} data-testid={`ob-waive-${t.code}`}>Waive</Button>}
            </TableCell>
          </TableRow>
        ))}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}


// =========================================================
// ONBOARDING TEMPLATE
// =========================================================
export function OnboardingTemplatePage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { API.get("/onboarding/templates").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="p-6 space-y-4" data-testid="ob-template-page">
      <h1 className="text-2xl font-bold">Template Checklist Onboarding</h1>
      <Card><CardContent className="pt-4"><Table><TableHeader><TableRow>
        <TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Scope</TableHead>
        <TableHead>Jml Item</TableHead><TableHead>Status</TableHead>
      </TableRow></TableHeader>
      <TableBody>{rows.map((r) => (
        <TableRow key={r.id} data-testid={`tmpl-row-${r.code}`}>
          <TableCell className="font-mono">{r.code}</TableCell>
          <TableCell>{r.name}</TableCell>
          <TableCell className="text-xs">{JSON.stringify(r.scope)}</TableCell>
          <TableCell>{(r.items || []).length}</TableCell>
          <TableCell>{r.active !== false ? "Aktif" : "Nonaktif"}</TableCell>
        </TableRow>
      ))}</TableBody></Table></CardContent></Card>
    </div>
  );
}


// =========================================================
// PROBATION
// =========================================================
export function ProbationPage() {
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [form, setForm] = useState({ employee_id: "", start_date: "", end_date: "" });
  const reload = () => API.get("/onboarding-probation").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); API.get("/hr/employees").then((r) => setEmps(r.data)).catch(() => {}); }, []);
  const submit = async () => {
    try { await API.post("/onboarding-probation/start", form); toast.success("Probation dimulai"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const result = async (pid, res) => {
    const notes = window.prompt("Catatan (opsional):") || "";
    const body = { result: res, notes };
    if (res === "EXTEND") { body.extend_end_date = window.prompt("Extend end date (YYYY-MM-DD):"); if (!body.extend_end_date) return; }
    try { await API.post(`/onboarding-probation/${pid}/result`, body); toast.success("OK"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="probation-page">
      <h1 className="text-2xl font-bold">Probation</h1>
      <Card><CardHeader><CardTitle>Mulai Probation</CardTitle></CardHeader><CardContent className="grid grid-cols-4 gap-3">
        <div><Label>Karyawan</Label>
          <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
            <SelectTrigger data-testid="prob-emp"><SelectValue placeholder="Pilih" /></SelectTrigger>
            <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} — {e.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Mulai</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="prob-start" /></div>
        <div><Label>Berakhir</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="prob-end" /></div>
        <Button onClick={submit} data-testid="prob-submit">Mulai</Button>
      </CardContent></Card>
      <Card><CardContent className="pt-4"><Table><TableHeader><TableRow>
        <TableHead>No</TableHead><TableHead>Karyawan</TableHead><TableHead>Periode</TableHead>
        <TableHead>Status</TableHead><TableHead>Result</TableHead><TableHead>Aksi</TableHead>
      </TableRow></TableHeader>
      <TableBody>{rows.map((r) => (
        <TableRow key={r.id} data-testid={`prob-row-${r.number}`}>
          <TableCell className="font-mono">{r.number}</TableCell>
          <TableCell>{r.employee_name}</TableCell>
          <TableCell className="text-xs">{r.start_date} → {r.end_date}</TableCell>
          <TableCell><Badge className={badgeColor(r.status)}>{r.status}</Badge></TableCell>
          <TableCell>{r.result ? <Badge className={badgeColor(r.result)}>{r.result}</Badge> : "-"}</TableCell>
          <TableCell className="space-x-1">
            {r.status === "ACTIVE" && <>
              <Button size="sm" onClick={() => result(r.id, "PASS")}>PASS</Button>
              <Button size="sm" variant="outline" onClick={() => result(r.id, "EXTEND")}>EXTEND</Button>
              <Button size="sm" variant="destructive" onClick={() => result(r.id, "FAIL")}>FAIL</Button>
            </>}
          </TableCell>
        </TableRow>
      ))}</TableBody></Table></CardContent></Card>
    </div>
  );
}


// =========================================================
// OFFBOARDING HUB
// =========================================================
export function OffboardingHub() {
  const [dash, setDash] = useState({});
  useEffect(() => { API.get("/offboarding-dashboard").then((r) => setDash(r.data)).catch(() => {}); }, []);
  return (
    <div className="p-6 space-y-4" data-testid="offboarding-hub">
      <h1 className="text-3xl font-bold">Offboarding — Tahap 8G</h1>
      <div className="grid md:grid-cols-5 gap-3">
        <Card><CardHeader><CardTitle className="text-sm">In Progress</CardTitle></CardHeader><CardContent className="text-2xl font-bold" data-testid="stat-off-inprog">{dash.offboarding_in_progress ?? "-"}</CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Pending Clearance</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-amber-600">{dash.pending_clearance ?? "-"}</CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Asset Pending</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{dash.asset_return_pending ?? "-"}</CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Payroll Pending</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{dash.final_payroll_pending ?? "-"}</CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm">Access Pending</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-red-600">{dash.access_revocation_pending ?? "-"}</CardContent></Card>
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <a href="/offboarding/proses"><Card className="hover:shadow-md" data-testid="link-off-list"><CardHeader><CardTitle>Proses Offboarding</CardTitle></CardHeader><CardContent>Mulai / kelola offboarding.</CardContent></Card></a>
        <a href="/offboarding/aset"><Card className="hover:shadow-md" data-testid="link-off-assets"><CardHeader><CardTitle>Asset Assignments</CardTitle></CardHeader><CardContent>Kelola aset yang diserahkan.</CardContent></Card></a>
        <a href="/offboarding/integritas"><Card className="hover:shadow-md" data-testid="link-off-int"><CardHeader><CardTitle>Integritas Offboarding</CardTitle></CardHeader><CardContent>Cek konsistensi.</CardContent></Card></a>
      </div>
    </div>
  );
}


// =========================================================
// OFFBOARDING LIST + INITIATE
// =========================================================
export function OffboardingListPage() {
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [form, setForm] = useState({ employee_id: "", reason: "RESIGN", reason_note: "", notice_date: "", last_working_date: "" });
  const reload = () => API.get("/offboarding").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); API.get("/hr/employees").then((r) => setEmps(r.data)).catch(() => {}); }, []);
  const submit = async () => {
    try { await API.post("/offboarding/initiate", form); toast.success("Offboarding diajukan"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="offboarding-list-page">
      <h1 className="text-2xl font-bold">Proses Offboarding</h1>
      <Card><CardHeader><CardTitle>Initiate Offboarding</CardTitle></CardHeader><CardContent className="grid grid-cols-5 gap-3">
        <div><Label>Karyawan</Label>
          <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
            <SelectTrigger data-testid="off-emp"><SelectValue placeholder="Pilih" /></SelectTrigger>
            <SelectContent>{emps.filter((e) => e.status === "ACTIVE" || e.status === "PROBATION").map((e) => <SelectItem key={e.id} value={e.id}>{e.code} — {e.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Alasan</Label>
          <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
            <SelectTrigger data-testid="off-reason"><SelectValue /></SelectTrigger>
            <SelectContent>{["RESIGN", "TERMINATION", "CONTRACT_END", "RETIREMENT", "OTHER"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Tgl Notice</Label><Input type="date" value={form.notice_date} onChange={(e) => setForm({ ...form, notice_date: e.target.value })} data-testid="off-notice" /></div>
        <div><Label>Last Working Date</Label><Input type="date" value={form.last_working_date} onChange={(e) => setForm({ ...form, last_working_date: e.target.value })} data-testid="off-lwd" /></div>
        <Button onClick={submit} data-testid="off-submit">Ajukan</Button>
        <div className="col-span-5"><Label>Catatan</Label><Textarea value={form.reason_note} onChange={(e) => setForm({ ...form, reason_note: e.target.value })} /></div>
      </CardContent></Card>
      <Card><CardContent className="pt-4"><Table><TableHeader><TableRow>
        <TableHead>No</TableHead><TableHead>Karyawan</TableHead><TableHead>Alasan</TableHead>
        <TableHead>LWD</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead>
      </TableRow></TableHeader>
      <TableBody>{rows.map((r) => (
        <TableRow key={r.id} data-testid={`off-row-${r.number}`}>
          <TableCell className="font-mono">{r.number}</TableCell>
          <TableCell>{r.employee_name}</TableCell>
          <TableCell>{r.reason}</TableCell>
          <TableCell>{r.last_working_date}</TableCell>
          <TableCell><Badge className={badgeColor(r.status)}>{r.status}</Badge></TableCell>
          <TableCell><a className="text-blue-600 underline" href={`/offboarding/proses/${r.id}`}>Detail</a></TableCell>
        </TableRow>
      ))}</TableBody></Table></CardContent></Card>
    </div>
  );
}


// =========================================================
// OFFBOARDING DETAIL
// =========================================================
export function OffboardingDetailPage() {
  const oid = window.location.pathname.split("/").pop();
  const [off, setOff] = useState(null);
  const [preview, setPreview] = useState(null);
  const [ei, setEi] = useState({ interview_date: "", reason_category: "", feedback: "", recommendation: "" });
  const reload = () => API.get(`/offboarding/${oid}`).then((r) => setOff(r.data)).catch(() => {});
  useEffect(() => { reload(); }, [oid]);
  if (!off) return <div className="p-6" data-testid="off-detail-loading">Memuat…</div>;
  const trans = async (ep, body = {}) => {
    try { await API.post(`/offboarding/${oid}/${ep}`, body); toast.success("OK"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const approveClearance = async (code) => {
    try { await API.post(`/offboarding/${oid}/clearances/${code}/approve`, {}); toast.success("Approved"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const overrideClearance = async (code) => {
    const reason = window.prompt("Alasan override (min 5 karakter):");
    if (!reason || reason.length < 5) return;
    try { await API.post(`/offboarding/${oid}/clearances/${code}/override`, { reason }); toast.success("Overridden"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const doPreview = async () => {
    try { const r = await API.get(`/offboarding/${oid}/final-payroll-preview`); setPreview(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal preview"); }
  };
  const doCompute = async () => {
    try { await API.post(`/offboarding/${oid}/final-payroll-compute`, { include_severance: true, manual_adjustments: [] }); toast.success("Final payroll computed"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  const submitEi = async () => {
    try { await API.post(`/offboarding/${oid}/exit-interview`, ei); toast.success("Exit interview tersimpan"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="off-detail-page">
      <h1 className="text-2xl font-bold">Offboarding {off.number}</h1>
      <Card><CardContent className="grid grid-cols-5 gap-3 pt-4">
        <div><Label className="text-xs">Karyawan</Label><div>{off.employee_name}</div></div>
        <div><Label className="text-xs">Alasan</Label><div>{off.reason}</div></div>
        <div><Label className="text-xs">LWD</Label><div>{off.last_working_date}</div></div>
        <div><Label className="text-xs">Status</Label><div><Badge className={badgeColor(off.status)}>{off.status}</Badge></div></div>
        <div className="space-x-2">
          {off.status === "INITIATED" && <Button size="sm" onClick={() => trans("to-clearance")} data-testid="off-to-clear">Ke Clearance</Button>}
          {off.status === "CLEARANCE" && <Button size="sm" onClick={() => trans("to-final-payroll")} data-testid="off-to-fp">Ke Final Payroll</Button>}
          {off.status === "FINAL_PAYROLL" && <Button size="sm" onClick={() => trans("to-asset-return")} data-testid="off-to-ar">Ke Asset Return</Button>}
          {off.status === "ASSET_RETURN" && <Button size="sm" onClick={() => trans("to-access-revocation")} data-testid="off-to-avr">Ke Access Revoke</Button>}
          {off.status === "ACCESS_REVOCATION" && <>
            <Button size="sm" variant="outline" onClick={() => trans("revoke-access", { revoke_reason: "Offboarding" })} data-testid="off-revoke">Revoke Access</Button>
            <Button size="sm" onClick={() => trans("complete")} data-testid="off-complete">Complete</Button>
          </>}
        </div>
      </CardContent></Card>

      <Card><CardHeader><CardTitle>Clearance Checklist ({off.clearance_status})</CardTitle></CardHeader><CardContent>
        <Table><TableHeader><TableRow>
          <TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Kritikal</TableHead>
          <TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead>
        </TableRow></TableHeader>
        <TableBody>{(off.clearances || []).map((c) => (
          <TableRow key={c.code} data-testid={`clr-row-${c.code}`}>
            <TableCell className="font-mono">{c.code}</TableCell>
            <TableCell>{c.name}</TableCell>
            <TableCell>{c.critical ? "Ya" : "-"}</TableCell>
            <TableCell>{c.approver_role || "-"}</TableCell>
            <TableCell><Badge className={badgeColor(c.status)}>{c.status}</Badge>{c.override_reason && <div className="text-xs italic">"{c.override_reason}"</div>}</TableCell>
            <TableCell className="space-x-1">
              {c.status === "PENDING" && <>
                <Button size="sm" onClick={() => approveClearance(c.code)} data-testid={`clr-approve-${c.code}`}>Approve</Button>
                <Button size="sm" variant="outline" onClick={() => overrideClearance(c.code)} data-testid={`clr-override-${c.code}`}>Override</Button>
              </>}
            </TableCell>
          </TableRow>
        ))}</TableBody></Table>
      </CardContent></Card>

      <Card><CardHeader><CardTitle>Final Payroll ({off.final_payroll_status})</CardTitle></CardHeader><CardContent>
        <div className="space-x-2 mb-3">
          <Button size="sm" onClick={doPreview} data-testid="fp-preview">Preview</Button>
          <Button size="sm" onClick={doCompute} data-testid="fp-compute">Compute & Simpan</Button>
        </div>
        {preview && <pre className="text-xs bg-slate-100 p-3 rounded max-h-80 overflow-auto">{JSON.stringify(preview.totals, null, 2)}</pre>}
        {off.final_settlement && <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Total Earnings</Label><div className="font-bold">{rp(off.final_settlement.totals?.total_earnings)}</div></div>
          <div><Label className="text-xs">Total Deductions</Label><div className="font-bold">{rp(off.final_settlement.totals?.total_deductions)}</div></div>
          <div><Label className="text-xs">Net Settlement</Label><div className="font-bold text-emerald-600">{rp(off.final_settlement.totals?.net_settlement)}</div></div>
        </div>}
      </CardContent></Card>

      <Card><CardHeader><CardTitle>Exit Interview</CardTitle></CardHeader><CardContent className="grid grid-cols-2 gap-3">
        <div><Label>Tgl Interview</Label><Input type="date" value={ei.interview_date} onChange={(e) => setEi({ ...ei, interview_date: e.target.value })} data-testid="ei-date" /></div>
        <div><Label>Kategori Alasan</Label><Input value={ei.reason_category} onChange={(e) => setEi({ ...ei, reason_category: e.target.value })} /></div>
        <div className="col-span-2"><Label>Feedback</Label><Textarea value={ei.feedback} onChange={(e) => setEi({ ...ei, feedback: e.target.value })} data-testid="ei-feedback" /></div>
        <div className="col-span-2"><Label>Rekomendasi</Label><Textarea value={ei.recommendation} onChange={(e) => setEi({ ...ei, recommendation: e.target.value })} /></div>
        <Button onClick={submitEi} data-testid="ei-submit">Simpan Exit Interview</Button>
        {off.exit_interview && <div className="col-span-2 text-sm text-slate-600 italic">Terekam: {off.exit_interview.interview_date} — status {off.exit_interview_status}</div>}
      </CardContent></Card>
    </div>
  );
}


// =========================================================
// ASSET ASSIGNMENTS
// =========================================================
export function AssetAssignmentsPage() {
  const [rows, setRows] = useState([]);
  const [emps, setEmps] = useState([]);
  const [form, setForm] = useState({ employee_id: "", asset_name: "", serial_number: "", assigned_date: "", condition_at_assign: "GOOD" });
  const reload = () => API.get("/offboarding-asset-assignments").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { reload(); API.get("/hr/employees").then((r) => setEmps(r.data)).catch(() => {}); }, []);
  const submit = async () => {
    try { await API.post("/offboarding-asset-assignments", form); toast.success("Aset di-assign"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };
  return (
    <div className="p-6 space-y-4" data-testid="asset-assign-page">
      <h1 className="text-2xl font-bold">Asset Assignments</h1>
      <Card><CardHeader><CardTitle>Assign Aset</CardTitle></CardHeader><CardContent className="grid grid-cols-5 gap-3">
        <div><Label>Karyawan</Label>
          <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
            <SelectTrigger data-testid="asset-emp"><SelectValue placeholder="Pilih" /></SelectTrigger>
            <SelectContent>{emps.map((e) => <SelectItem key={e.id} value={e.id}>{e.code} — {e.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Nama Aset</Label><Input value={form.asset_name} onChange={(e) => setForm({ ...form, asset_name: e.target.value })} data-testid="asset-name" /></div>
        <div><Label>Serial No</Label><Input value={form.serial_number} onChange={(e) => setForm({ ...form, serial_number: e.target.value })} /></div>
        <div><Label>Tgl Assign</Label><Input type="date" value={form.assigned_date} onChange={(e) => setForm({ ...form, assigned_date: e.target.value })} /></div>
        <Button onClick={submit} data-testid="asset-submit">Assign</Button>
      </CardContent></Card>
      <Card><CardContent className="pt-4"><Table><TableHeader><TableRow>
        <TableHead>Karyawan</TableHead><TableHead>Aset</TableHead><TableHead>SN</TableHead>
        <TableHead>Assign Date</TableHead><TableHead>Return Date</TableHead>
        <TableHead>Kondisi Return</TableHead><TableHead>Status</TableHead>
      </TableRow></TableHeader>
      <TableBody>{rows.map((r) => (
        <TableRow key={r.id} data-testid={`asset-row-${r.id.slice(0, 8)}`}>
          <TableCell>{r.employee_name}</TableCell>
          <TableCell>{r.asset_name}</TableCell>
          <TableCell className="font-mono text-xs">{r.serial_number || "-"}</TableCell>
          <TableCell>{r.assigned_date}</TableCell>
          <TableCell>{r.returned_date || "-"}</TableCell>
          <TableCell>{r.condition_at_return || "-"}</TableCell>
          <TableCell><Badge className={badgeColor(r.status)}>{r.status}</Badge></TableCell>
        </TableRow>
      ))}</TableBody></Table></CardContent></Card>
    </div>
  );
}


// =========================================================
// INTEGRITY
// =========================================================
export function OnboardingIntegrityPage() {
  const [d, setD] = useState(null);
  useEffect(() => { API.get("/onboarding-integrity").then((r) => setD(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Gagal")); }, []);
  if (!d) return <div className="p-6" data-testid="ob-int-loading">Memuat…</div>;
  return (
    <div className="p-6 space-y-4" data-testid="ob-integrity-page">
      <h1 className="text-2xl font-bold">Integritas Onboarding</h1>
      <Card><CardContent className="pt-4"><Badge className={badgeColor(d.overall)}>Overall: {d.overall}</Badge></CardContent></Card>
      <Card><CardContent className="pt-4"><Table><TableHeader><TableRow><TableHead>Check</TableHead><TableHead>Status</TableHead><TableHead>Info</TableHead></TableRow></TableHeader>
      <TableBody>{(d.checks || []).map((c, i) => (
        <TableRow key={i}><TableCell>{c.name}</TableCell>
          <TableCell><Badge className={badgeColor(c.status)}>{c.status}</Badge></TableCell>
          <TableCell className="text-xs">{JSON.stringify({ ...c, name: undefined, status: undefined })}</TableCell>
        </TableRow>
      ))}</TableBody></Table></CardContent></Card>
    </div>
  );
}


export function OffboardingIntegrityPage() {
  const [d, setD] = useState(null);
  useEffect(() => { API.get("/offboarding-integrity").then((r) => setD(r.data)).catch((e) => toast.error(e.response?.data?.detail || "Gagal")); }, []);
  if (!d) return <div className="p-6" data-testid="off-int-loading">Memuat…</div>;
  return (
    <div className="p-6 space-y-4" data-testid="off-integrity-page">
      <h1 className="text-2xl font-bold">Integritas Offboarding</h1>
      <Card><CardContent className="pt-4"><Badge className={badgeColor(d.overall)}>Overall: {d.overall}</Badge></CardContent></Card>
      <Card><CardContent className="pt-4"><Table><TableHeader><TableRow><TableHead>Check</TableHead><TableHead>Status</TableHead><TableHead>Info</TableHead></TableRow></TableHeader>
      <TableBody>{(d.checks || []).map((c, i) => (
        <TableRow key={i}><TableCell>{c.name}</TableCell>
          <TableCell><Badge className={badgeColor(c.status)}>{c.status}</Badge></TableCell>
          <TableCell className="text-xs">{JSON.stringify({ ...c, name: undefined, status: undefined })}</TableCell>
        </TableRow>
      ))}</TableBody></Table></CardContent></Card>
    </div>
  );
}
